/**
 * PASTEL PALLET — ANIMATIONS JS
 * Scroll reveals, hero carousel, dropdowns, interactive animations
 */

'use strict';

/* ─── 1. SCROLL REVEAL (IntersectionObserver) ─── */
const ScrollReveal = {
  observer: null,
  init() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = el.dataset.revealDelay || (el.dataset.revealIndex ? el.dataset.revealIndex * 100 : 0);
          setTimeout(() => el.classList.add('is-visible'), parseInt(delay));
          this.observer.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    this.observe();
  },

  observe() {
    document.querySelectorAll('.pp-reveal').forEach((el, i) => {
      el.dataset.revealIndex = i % 8;
      this.observer.observe(el);
    });
  },

  refresh() {
    this.observe();
  }
};

/* ─── 2. HERO CAROUSEL ─── */
const HeroCarousel = {
  slides: [],
  dots: [],
  current: 0,
  timer: null,
  interval: 5000,
  transitioning: false,

  init() {
    this.slides = Array.from(document.querySelectorAll('.pp-hero__slide'));
    this.dots = Array.from(document.querySelectorAll('.pp-hero__dot'));
    if (this.slides.length < 2) {
      if (this.slides[0]) this.slides[0].classList.add('is-active');
      return;
    }

    this.goTo(0);
    this.startAuto();

    const hero = document.querySelector('.pp-hero');
    hero?.addEventListener('mouseenter', () => this.stopAuto());
    hero?.addEventListener('mouseleave', () => this.startAuto());

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.goTo(i); this.stopAuto(); this.startAuto(); });
    });

    let startX = 0;
    hero?.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    hero?.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? this.next() : this.prev(); }
    });
  },

  goTo(index) {
    if (this.transitioning) return;
    this.transitioning = true;
    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');
    this.current = (index + this.slides.length) % this.slides.length;
    this.slides[this.current]?.classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');
    setTimeout(() => { this.transitioning = false; }, 1200);
  },

  next() { this.goTo(this.current + 1); },
  prev() { this.goTo(this.current - 1); },
  startAuto() { this.timer = setInterval(() => this.next(), this.interval); },
  stopAuto() { clearInterval(this.timer); }
};

/* ─── 3. ANNOUNCEMENT BAR MARQUEE DUPLICATION ─── */
function initMarquee() {
  const track = document.querySelector('.pp-announcement-bar__track');
  if (!track) return;
  const clone = track.cloneNode(true);
  clone.setAttribute('aria-hidden', 'true');
  track.parentElement.appendChild(clone);
}

/* ─── 4. MEGA DROPDOWN ─── */
function initMegaDropdown() {
  const megaItem = document.querySelector('.pp-header__nav-item--mega');
  const dropdown = document.querySelector('.pp-mega-dropdown');
  if (!megaItem || !dropdown) return;

  let hideTimer;
  const show = () => { clearTimeout(hideTimer); dropdown.classList.add('is-visible'); };
  const hide = () => { hideTimer = setTimeout(() => dropdown.classList.remove('is-visible'), 200); };

  megaItem.addEventListener('mouseenter', show);
  megaItem.addEventListener('mouseleave', hide);
  dropdown.addEventListener('mouseenter', show);
  dropdown.addEventListener('mouseleave', hide);
}

/* ─── 5. STAGGER CARD REVEALS ─── */
function staggerCards(containerSelector, cardSelector, delay = 80) {
  const containers = document.querySelectorAll(containerSelector);
  containers.forEach(container => {
    container.querySelectorAll(cardSelector).forEach((card, i) => {
      card.classList.add('pp-reveal');
      card.dataset.revealDelay = i * delay;
    });
  });
}

/* ─── 6. INSTAGRAM HOVER ─── */
function initInstagram() {
  document.querySelectorAll('.pp-instagram-block').forEach(block => {
    block.addEventListener('mouseenter', () => block.classList.add('is-hovered'));
    block.addEventListener('mouseleave', () => block.classList.remove('is-hovered'));
  });
}

/* ─── 7. OCCASION BUBBLE DRAG SCROLL ─── */
function initOccasionDrag() {
  const track = document.querySelector('.pp-occasion__track-wrap');
  if (!track) return;
  let isDragging = false, startX, scrollLeft;

  track.addEventListener('mousedown', e => {
    isDragging = true;
    startX = e.pageX - track.offsetLeft;
    scrollLeft = track.scrollLeft;
    track.style.cursor = 'grabbing';
  });
  track.addEventListener('mouseleave', () => { isDragging = false; track.style.cursor = ''; });
  track.addEventListener('mouseup', () => { isDragging = false; track.style.cursor = ''; });
  track.addEventListener('mousemove', e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - track.offsetLeft;
    const walk = (x - startX) * 2;
    track.scrollLeft = scrollLeft - walk;
  });
}

/* ─── 8. SMOOTH SECTION ENTRY ANIMATIONS ─── */
function addRevealClasses() {
  const sections = [
    '.pp-trust-bar',
    '.pp-section-head',
    '.pp-story__text',
    '.pp-story__img-wrap',
    '.pp-newsletter__content',
    '.pp-testimonial-card',
    '.pp-instagram-block'
  ];
  sections.forEach(sel => {
    document.querySelectorAll(sel).forEach(el => {
      if (!el.classList.contains('pp-reveal')) el.classList.add('pp-reveal');
    });
  });
}

/* ─── 9. PRODUCT IMAGE HOVER (SWAP) ─── */
function initProductImageSwap() {
  document.querySelectorAll('.pp-product-card').forEach(card => {
    const primary = card.querySelector('.pp-product-card__img--primary');
    const hover = card.querySelector('.pp-product-card__img--hover');
    if (!primary || !hover) return;
    card.addEventListener('mouseenter', () => {
      primary.style.opacity = '0';
      hover.style.opacity = '1';
    });
    card.addEventListener('mouseleave', () => {
      primary.style.opacity = '1';
      hover.style.opacity = '0';
    });
  });
}

/* ─── 10. PARALLAX PETALS ─── */
function initPetalParallax() {
  const petals = document.querySelectorAll('.pp-petal');
  if (!petals.length) return;
  window.addEventListener('mousemove', e => {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const dx = (e.clientX - cx) / cx;
    const dy = (e.clientY - cy) / cy;
    petals.forEach((p, i) => {
      const factor = (i + 1) * 6;
      p.style.transform = `translate(${dx * factor}px, ${dy * factor}px)`;
    });
  }, { passive: true });
}

/* ─── 11. HEADER NAV UNDERLINE ─── */
function initNavUnderline() {
  document.querySelectorAll('.pp-header__nav-link').forEach(link => {
    if (!link.querySelector('.pp-nav-underline')) {
      const underline = document.createElement('span');
      underline.className = 'pp-nav-underline';
      underline.style.cssText = 'position:absolute;bottom:-1px;left:14px;right:14px;height:2px;background:var(--pp-gold);border-radius:2px;transform:scaleX(0);transform-origin:center;transition:transform 0.3s ease;';
      link.style.position = 'relative';
      link.appendChild(underline);
      link.addEventListener('mouseenter', () => underline.style.transform = 'scaleX(1)');
      link.addEventListener('mouseleave', () => underline.style.transform = 'scaleX(0)');
    }
  });
}

/* ─── 12. PRODUCT CARD ADD-TO-BAG SLIDE ─── */
function initCardActions() {
  document.querySelectorAll('.pp-product-card').forEach(card => {
    const actions = card.querySelector('.pp-product-card__actions');
    if (!actions) return;
    card.addEventListener('mouseenter', () => {
      actions.style.transform = 'translateY(0)';
      actions.style.opacity = '1';
    });
    card.addEventListener('mouseleave', () => {
      actions.style.transform = 'translateY(100%)';
      actions.style.opacity = '0';
    });
  });
}

/* ─── 13. NEWSLETTER FORM FEEDBACK ─── */
function initNewsletterForm() {
  document.querySelectorAll('.pp-newsletter-form').forEach(form => {
    form.addEventListener('submit', e => {
      const input = form.querySelector('.pp-newsletter__input');
      const btn = form.querySelector('.pp-newsletter__submit');
      if (btn) {
        const orig = btn.textContent;
        btn.textContent = 'Subscribed! 💛';
        btn.style.background = 'var(--pp-gold)';
        setTimeout(() => { btn.textContent = orig; btn.style.background = ''; }, 3000);
      }
    });
  });
}

/* ─── 14. COLLECTION CARD HOVER ─── */
function initCollectionCards() {
  document.querySelectorAll('.pp-collection-card').forEach(card => {
    card.addEventListener('mouseenter', () => {
      card.querySelector('.pp-collection-card__cta')?.style &&
        Object.assign(card.querySelector('.pp-collection-card__cta').style, { opacity: '1', transform: 'translateY(0)' });
    });
  });
}

/* ─── INIT ALL ─── */
document.addEventListener('DOMContentLoaded', () => {
  initMarquee();
  HeroCarousel.init();
  ScrollReveal.init();
  initMegaDropdown();
  initInstagram();
  initOccasionDrag();
  addRevealClasses();
  initProductImageSwap();
  initPetalParallax();
  initNavUnderline();
  initCardActions();
  initNewsletterForm();
  initCollectionCards();

  staggerCards('.pp-products__grid', '.pp-product-card', 80);
  staggerCards('.pp-collections__grid', '.pp-collection-card', 100);
  staggerCards('.pp-testimonials__grid', '.pp-testimonial-card', 120);
  staggerCards('.pp-instagram__grid', '.pp-instagram-block', 60);

  setTimeout(() => ScrollReveal.refresh(), 100);
});
